/**
 * EditableCell — click-to-edit table cell for the TenantsAdmin table.
 *
 * Two modes:
 *   • `text` — single-line input (brand, owner_name, owner_email, website)
 *   • `select` — native dropdown (tenant_type, plan)
 *
 * Saves on blur OR Enter; reverts on Escape. Loading spinner while
 * the PATCH is in flight. Optimistic UI — the parent rolls back if
 * the API rejects.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Check, X, Loader2 } from 'lucide-react';

export default function EditableCell({
  value,
  type = 'text',         // 'text' | 'select'
  options = [],          // for select
  onSave,                // async (newValue) => void; should throw on failure
  placeholder = '—',
  className = '',
  inputClassName = '',
  testId,
  ariaLabel,
  formatDisplay,         // optional (value) => ReactNode for the display row
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  // No `useEffect` to sync `draft ← value` — the parent rolls back on
  // PATCH failure by reverting `value`, and the next render of this
  // cell will not be editing (so the display path reads `value` again).
  // Skipping the effect keeps the strict set-state-in-effect lint happy.
  useEffect(() => { if (editing) setTimeout(() => inputRef.current?.focus(), 10); }, [editing]);

  const commit = async () => {
    const next = (draft ?? '').toString().trim();
    if (next === (value ?? '').toString().trim()) { setEditing(false); return; }
    setBusy(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (_) {
      // Parent already toasted — keep the editor open so the user can fix it.
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    if (type === 'select') {
      return (
        <div className="flex items-center gap-1.5">
          <select
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              else if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
            }}
            disabled={busy}
            data-testid={`${testId}-input`}
            className={`bg-zinc-900 border border-amber-500/40 rounded px-2 py-1 text-[12px] focus:outline-none focus:border-amber-500 ${inputClassName}`}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {busy && <Loader2 size={11} className="animate-spin text-amber-400" />}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
          }}
          disabled={busy}
          data-testid={`${testId}-input`}
          aria-label={ariaLabel || testId}
          className={`bg-zinc-900 border border-amber-500/40 rounded px-2 py-1 text-[12px] focus:outline-none focus:border-amber-500 min-w-0 ${inputClassName}`}
        />
        {busy
          ? <Loader2 size={11} className="animate-spin text-amber-400" />
          : (
            <>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); commit(); }}
                data-testid={`${testId}-save`}
                className="text-emerald-400 hover:text-emerald-300"
                aria-label="Save"
              >
                <Check size={12} />
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setDraft(value || ''); setEditing(false); }}
                data-testid={`${testId}-cancel`}
                className="text-zinc-500 hover:text-zinc-300"
                aria-label="Cancel"
              >
                <X size={12} />
              </button>
            </>
          )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => { setDraft(value || ''); setEditing(true); }}
      data-testid={testId}
      className={`group inline-flex items-center gap-1.5 text-left rounded px-1 -mx-1 hover:bg-amber-500/[0.08] transition cursor-text ${className}`}
      title="Click to edit"
    >
      <span className="min-w-0 truncate">
        {formatDisplay
          ? formatDisplay(value)
          : (value || <span className="text-zinc-600">{placeholder}</span>)}
      </span>
      <Pencil size={10} className="opacity-0 group-hover:opacity-50 transition shrink-0 text-amber-300" />
    </button>
  );
}
